import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'sonner';
import { emitTokensRefreshed } from './authBus';
import { isAiUnavailableError, AI_UNAVAILABLE_EVENT } from './aiErrors';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Resolve the active tenant id from either the standalone key or the
// persisted Zustand auth store. Either may be missing depending on
// which auth flow last ran (login / signup / refreshMemberships) — we
// accept whichever is present, with the standalone key winning ties.
const resolveActiveTenantId = (): string | null => {
  try {
    const direct = localStorage.getItem('activeTenantId');
    if (direct) return direct;
    const raw = localStorage.getItem('maie-emr-auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { activeTenantId?: string | null } };
    const fromStore = parsed?.state?.activeTenantId ?? null;
    // Re-hydrate the standalone key so subsequent reads are O(1).
    if (fromStore) {
      try {
        localStorage.setItem('activeTenantId', fromStore);
      } catch {
        /* ignore quota errors */
      }
    }
    return fromStore;
  } catch {
    return null;
  }
};

// Helper to clear auth and redirect
const clearAuthAndRedirect = () => {
  if (import.meta.env.DEV) {
    console.log('🚪 Clearing auth state and redirecting to login...');
  }
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('activeTenantId');

  // Also clear zustand persist storage
  localStorage.removeItem('auth-storage');
  localStorage.removeItem('maie-emr-auth'); // Auth store persistence

  window.location.href = '/login';
};

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Pass the active tenant id as a hint header. The backend uses this
    // (alongside JWT claims) to scope every request — so it MUST be sent
    // on every authenticated call. We resolve it from two locations to
    // survive the period after sign-up / membership refresh where one
    // copy may not yet be in sync with the other:
    //   1. Standalone `activeTenantId` localStorage key (set on login).
    //   2. The persisted Zustand auth store (`maie-emr-auth`) — survives
    //      refreshes via `partialize` and is updated by all flows.
    if (config.headers) {
      const tenantId = resolveActiveTenantId();
      if (tenantId) {
        config.headers['X-Tenant-Id'] = tenantId;
      }
    }

    // Debug logging for lab order updates. Gated to dev builds only — these
    // emit full request bodies (incl. lab-order PATCH = PHI) and must never
    // run in production.
    if (import.meta.env.DEV && config.url?.includes('/lab-orders/') && config.method === 'patch') {
      console.log('🔍 [apiClient] PATCH Request to:', config.url);
      console.log('🔍 [apiClient] Request data:', config.data);
      console.log('🔍 [apiClient] Request data stringified:', JSON.stringify(config.data, null, 2));

      // Check if specimens exist in the data
      if (config.data?.specimens) {
        console.log('🔍 [apiClient] Specimens in request:', config.data.specimens);
        console.log('🔍 [apiClient] Specimens type:', typeof config.data.specimens);
        console.log('🔍 [apiClient] Specimens isArray:', Array.isArray(config.data.specimens));
        console.log('🔍 [apiClient] Specimens length:', config.data.specimens.length);

        // Check each specimen
        config.data.specimens.forEach((s: unknown, idx: number) => {
          console.log(`🔍 [apiClient] Specimen ${idx}:`, s);
          console.log(`🔍 [apiClient] Specimen ${idx} type:`, typeof s);
          console.log(`🔍 [apiClient] Specimen ${idx} isArray:`, Array.isArray(s));
        });
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Coalesces concurrent 401s into a single /auth/refresh call. Without this
// lock, every in-flight request that 401s fires its own refresh, and a
// rotating-refresh-token backend will invalidate all but the last one.
let refreshInFlight: Promise<string> | null = null;

/**
 * Refresh the access token, at most once at a time.
 *
 * Exported because the auth store needs the SAME lock. It used to POST
 * /auth/refresh itself, which meant two independent refresh paths racing
 * on a rotating refresh token: the winner rotated it, the loser's token
 * was already dead, and its failure logged the user out. With a 10/min
 * throttle on the endpoint, a boot that fires several calls at once could
 * burn the allowance and 429 — which reads to the user as being kicked to
 * the login screen for no reason.
 */
export const performTokenRefresh = (): Promise<string> => {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) throw new Error('No refresh token');

    const response = await axios.post(
      `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'}/auth/refresh`,
      { refreshToken },
    );
    const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
      response.data.data;
    localStorage.setItem('accessToken', newAccessToken);
    localStorage.setItem('refreshToken', newRefreshToken);
    // Sync the zustand auth store so its persisted copy doesn't drift
    // from the standalone keys we just rotated. Without this, the next
    // SPA reload rehydrates a refresh token the backend has revoked,
    // and any flow reading `useAuthStore.getState().refreshToken`
    // (logout, refreshAuth) posts a dead token and gets the user
    // kicked. See services/authBus.ts for the full story.
    emitTokensRefreshed({
      accessToken: newAccessToken,
      refreshToken: newRefreshToken,
    });
    return newAccessToken;
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
};

// Surface rate-limit responses globally. Without this the user sees
// either nothing or a misleading "Failed to load X" toast from the
// calling code — and no hint that a brief retry will succeed. We
// dedupe via a fixed toast id so a burst of 429s collapses into a
// single visible toast that updates as the cooldown changes.
const RATE_LIMIT_TOAST_ID = 'api-rate-limit';
const showRateLimitToast = (error: AxiosError) => {
  const headers = error.response?.headers as
    | Record<string, string | undefined>
    | undefined;
  const retryAfterRaw =
    headers?.['retry-after'] ?? headers?.['Retry-After'];
  const seconds = Number(retryAfterRaw);
  const retryHint = Number.isFinite(seconds) && seconds > 0
    ? `Try again in ${Math.ceil(seconds)}s.`
    : 'Try again in a moment.';
  toast.warning('You are sending too many requests', {
    id: RATE_LIMIT_TOAST_ID,
    description: retryHint,
    duration: 6000,
  });
};

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (import.meta.env.DEV) {
      console.log('⚠️ API Error caught:', error.response?.status, error.config?.url);
    }

    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 429) {
      showRateLimitToast(error);
      return Promise.reject(error);
    }

    // An AI feature came back unavailable (disabled / out of quota / stack
    // down). Don't surface it globally — callers degrade to the manual
    // path. Nudge the capability map to re-sync so the affordance updates
    // to its correct disabled state without a page reload. The error still
    // rejects so the caller's own degrade handling runs.
    if (isAiUnavailableError(error)) {
      try {
        window.dispatchEvent(new CustomEvent(AI_UNAVAILABLE_EVENT));
      } catch {
        /* non-browser / SSR — ignore */
      }
      return Promise.reject(error);
    }

    // If error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (import.meta.env.DEV) {
        console.log('🔄 401 Error detected, attempting token refresh...');
      }
      originalRequest._retry = true;

      // Don't attempt token refresh for login/register/refresh endpoints
      const url = originalRequest.url || '';
      if (url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/refresh')) {
        if (import.meta.env.DEV) {
          console.log('⏭️ Skipping token refresh for auth endpoint:', url);
        }
        return Promise.reject(error);
      }

      try {
        const newAccessToken = await performTokenRefresh();
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }
        return apiClient(originalRequest);
      } catch (refreshError) {
        if (import.meta.env.DEV) {
          console.log('Token refresh failed, redirecting to login', refreshError);
        }
        clearAuthAndRedirect();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
